import React, { useState, useEffect } from 'react';
import { mainApi } from '../../../../../config/apiClient';
import GuideButton from '../../../../common/guide/GuideButton';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Paper,
  Radio
} from '@mui/material';
import {
  Close as CloseIcon,
  RestaurantMenu as RestaurantMenuIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

// Interfaces
interface QRMenu {
  id: string;
  name: string;
  branchName?: string;
}

interface QRMenuSelectionModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  selectedMenuId: string | null;
  onSelectMenu: (menuId: string, menuName: string) => void;
  onCancel?: () => void; // YENİ: Menü seçilmeden kapatıldığında çağrılacak
}

const QRMenuSelectionModal: React.FC<QRMenuSelectionModalProps> = ({
  open,
  onClose,
  companyId,
  selectedMenuId,
  onSelectMenu,
  onCancel
}) => {
  // State
  const [menus, setMenus] = useState<QRMenu[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tempSelectedMenuId, setTempSelectedMenuId] = useState<string | null>(selectedMenuId);

  // QR Menüleri yükle
  useEffect(() => {
    const fetchMenus = async () => {
      if (!companyId || !open) return;

      setLoading(true);
      setError(null);

      try {
        const response = await mainApi.get(`/api/QRMenu/menu-definitions`);
        const result = response.data;
        if (result.success && result.data) {
          const menuList: QRMenu[] = result.data.map((menu: any) => ({
            id: menu.id,
            name: menu.name || 'İsimsiz Menü',
            branchName: menu.branchName
          }));
          
          setMenus(menuList);
        } else {
          setError('Menüler yüklenirken hata oluştu.');
        }
      } catch (error) {
        console.error('❌ QR Menü yükleme hatası:', error);
        setError('Menüler yüklenirken hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      setTempSelectedMenuId(selectedMenuId);
      fetchMenus();
    }
  }, [companyId, open, selectedMenuId]);

  // Menü seç
  const handleSelectMenu = (menuId: string) => {
    setTempSelectedMenuId(menuId);
  };

  // Kaydet ve kapat
  const handleSave = () => {
    if (tempSelectedMenuId) {
      const selectedMenu = menus.find(m => m.id === tempSelectedMenuId);
      if (selectedMenu) {
        onSelectMenu(tempSelectedMenuId, selectedMenu.name);
      }
    }
    onClose();
  };

  // İptal
  const handleCancel = () => {
    setTempSelectedMenuId(selectedMenuId);
    
    // Eğer daha önce seçili menü yoksa ve kullanıcı yeni seçim yapmadan çıkıyorsa
    if (!selectedMenuId && onCancel) {
      onCancel(); // Parent'a bildir (switch'i kapat)
    }
    
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '400px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        bgcolor: 'primary.main', 
        color: 'white',
        py: 2,
        px: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <RestaurantMenuIcon sx={{ mr: 1.5, fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            Menü Seçimi
          </Typography>
          <GuideButton guideKey="sales.qrMenuSelection" color="inherit" size="small" tabIndex={-1} sx={{ ml: 0.5, width: 40, height: 40 }} />
        </Box>
        <IconButton onClick={handleCancel} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 3, minHeight: '300px' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <CircularProgress size={60} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : menus.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
            <RestaurantMenuIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              QR Menü Bulunamadı
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Henüz tanımlanmış bir QR menü bulunmuyor.
            </Typography>
          </Paper>
        ) : (
          <>
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold', width: '60px' }}>Seç</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Menü Adı</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Şube</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {menus.map((menu) => (
                    <TableRow 
                      key={menu.id}
                      hover
                      selected={tempSelectedMenuId === menu.id}
                      onClick={() => handleSelectMenu(menu.id)}
                      sx={{ 
                        cursor: 'pointer',
                        '&.Mui-selected': {
                          bgcolor: 'primary.light',
                          '&:hover': {
                            bgcolor: 'primary.light',
                          }
                        }
                      }}
                    >
                      <TableCell>
                        <Radio
                          checked={tempSelectedMenuId === menu.id}
                          onChange={() => handleSelectMenu(menu.id)}
                          value={menu.id}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <RestaurantMenuIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              {menu.name}
                            </Typography>
                            {tempSelectedMenuId === menu.id && (
                              <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                                <CheckCircleIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                Seçildi
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {menu.branchName || 'Şube Atanmamış'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
        <Button 
          onClick={handleCancel}
          variant="outlined"
          size="large"
        >
          İptal
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          size="large"
          disabled={!tempSelectedMenuId || loading}
          startIcon={tempSelectedMenuId ? <CheckCircleIcon /> : null}
        >
          {tempSelectedMenuId ? 'Menüyü Seç' : 'Lütfen Menü Seçin'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QRMenuSelectionModal;

